import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { SignalingService } from '../services/signaling.service';
import { WebRTCService } from '../services/webrtc.service';
import LiveStreamPlayer from '../components/LiveStreamPlayer';
import { handlePacketMonitorMessage } from '../services/cloud-packet-monitor';

/**
 * Full-screen camera stream page (no layout chrome).
 * Opened in a new tab from the camera grid fullscreen button.
 */
export default function CameraStreamPage() {
  const { nodeId, cameraId } = useParams<{ nodeId: string; cameraId: string }>();

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [connectionState, setConnectionState] = useState<string>('new');
  const [cameraStateOverride, setCameraStateOverride] = useState<string>('');

  const signalingRef = useRef<SignalingService | null>(null);
  const webrtcRef = useRef<WebRTCService | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const cancelledRef = useRef(false);
  const waitingStreamTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallCountRef = useRef(0);
  const initConnectionRef = useRef<() => void>(() => {});
  // Terminal: SFU denied this camera for the acting user (no RBAC access).
  // Suppresses all reconnect/retry so the tile shows "access denied" instead of looping.
  const deniedRef = useRef(false);

  const MAX_RECONNECT = 20;
  const RECONNECT_BASE = 2000;
  const RECONNECT_MAX = 30000;

  const teardown = useCallback(() => {
    signalingRef.current?.close();
    webrtcRef.current?.close();
    signalingRef.current = null;
    webrtcRef.current = null;
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (cancelledRef.current || deniedRef.current) return;
    // Coalesce the multiple triggers (signaling close, pc 'failed', init catch)
    // that can fire on one flap: clear any pending timer before re-arming so we
    // never stack reconnects that each tear down and rebuild the signaling WS.
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectAttemptsRef.current++;
    // Exponential backoff capped at RECONNECT_MAX, then keep retrying at that
    // interval FOREVER — giving up after MAX_RECONNECT left the player permanently
    // dead after any outage longer than the backoff ladder (slow networks).
    const attempt = Math.min(reconnectAttemptsRef.current, MAX_RECONNECT);
    const delay = Math.min(RECONNECT_BASE * 2 ** (attempt - 1), RECONNECT_MAX);
    setConnectionState('connecting');
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      if (!cancelledRef.current) initConnectionRef.current();
    }, delay * (0.75 + Math.random() * 0.5));
  }, []);

  const handleMsg = useCallback((msg: any) => {
    if (msg.type?.startsWith('packet_monitor_')) { handlePacketMonitorMessage(msg); return; }
    const webrtc = webrtcRef.current;
    if (!webrtc) return;
    switch (msg.type) {
      case 'offer': webrtc.handleOffer(msg.sdp); break;
      case 'ice-candidate': webrtc.handleIceCandidate({ candidate: msg.candidate, sdpMid: msg.sdpMid, sdpMLineIndex: msg.sdpMLineIndex }); break;
      case 'camera-status':
        if (msg.cameraId === cameraId) {
          if (msg.status === 'denied') {
            deniedRef.current = true;
            if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
            if (waitingStreamTimerRef.current) { clearTimeout(waitingStreamTimerRef.current); waitingStreamTimerRef.current = null; }
            setStream(null);
            setCameraStateOverride('access_denied');
          } else if (msg.status === 'offline') setCameraStateOverride('camera_unreachable');
          else if (msg.status === 'online') setCameraStateOverride('');
        }
        break;
    }
  }, [cameraId]);

  const initConnection = useCallback(async () => {
    if (!nodeId || !cameraId || cancelledRef.current) return;
    try {
      teardown();
      const signaling = new SignalingService(nodeId);
      signalingRef.current = signaling;

      const { turnCredentials } = await signaling.connect(
        (msg) => handleMsg(msg),
        () => { if (!cancelledRef.current) { setConnectionState('disconnected'); scheduleReconnect(); } }
      );
      if (cancelledRef.current) { signaling.close(); return; }

      const webrtc = new WebRTCService(signaling);
      webrtcRef.current = webrtc;

      await webrtc.initialize(
        turnCredentials,
        (event: RTCTrackEvent) => {
          if (cancelledRef.current || deniedRef.current) return;
          const mediaStream = event.streams?.[0] || new MediaStream([event.track]);
          setStream(mediaStream);
          if (waitingStreamTimerRef.current) { clearTimeout(waitingStreamTimerRef.current); waitingStreamTimerRef.current = null; }
          if (stallCountRef.current === 0) setCameraStateOverride('');
        },
        (state: string) => {
          if (cancelledRef.current || deniedRef.current) return;
          setConnectionState(state);
          if (state === 'connected') {
            reconnectAttemptsRef.current = 0;
            if (!waitingStreamTimerRef.current) {
              waitingStreamTimerRef.current = setTimeout(() => {
                waitingStreamTimerRef.current = null;
                if (!cancelledRef.current) setCameraStateOverride('camera_unreachable');
              }, 20000);
            }
          } else if (state === 'failed') {
            scheduleReconnect();
          }
        }
      );
      if (cancelledRef.current) { webrtc.close(); signaling.close(); return; }

      signaling.subscribe([cameraId]);
      setConnectionState('connecting');
    } catch {
      if (!cancelledRef.current) scheduleReconnect();
    }
  }, [nodeId, cameraId, teardown, scheduleReconnect, handleMsg]);

  initConnectionRef.current = initConnection;

  useEffect(() => {
    cancelledRef.current = false;
    deniedRef.current = false;
    initConnection();
    return () => {
      cancelledRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (waitingStreamTimerRef.current) clearTimeout(waitingStreamTimerRef.current);
      teardown();
    };
  }, [nodeId, cameraId]);

  const handleStall = useCallback(() => {
    if (deniedRef.current) return;
    stallCountRef.current++;
    setCameraStateOverride('camera_unreachable');
    setStream(null);
  }, []);

  const handlePlaying = useCallback(() => {
    if (stallCountRef.current === 0) return;
    stallCountRef.current = 0;
    setCameraStateOverride('');
  }, []);

  const handleRetry = useCallback(() => {
    if (deniedRef.current) return; // RBAC denial is terminal
    const signaling = signalingRef.current;
    if (!signaling || !cameraId) return;
    stallCountRef.current = 0;
    setCameraStateOverride('');
    signaling.unsubscribe([cameraId]);
    setStream(null);
    setTimeout(() => signalingRef.current?.subscribe([cameraId]), 500);
  }, [cameraId]);

  return (
    <div className="h-screen w-screen bg-black flex items-center justify-center">
      <div className="w-full h-full">
        <LiveStreamPlayer
          stream={stream}
          connectionState={cameraStateOverride || connectionState}
          onStalled={handleStall}
          onPlaying={handlePlaying}
          onRetry={handleRetry}
        />
      </div>
    </div>
  );
}
